import { TOOL_COUNT } from '../../../content/toolCount';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRightOutlined, SafetyOutlined, SettingOutlined, EyeOutlined } from '@ant-design/icons';
import { SEO_DEFAULTS } from '../../../utils/seoConfig';
import { generateFAQSchema, generateBreadcrumbSchema, JsonLd } from '../../../utils/structuredData';
import { COST_FAQ_ANSWER } from '../../../content/pricingFigures';
import { BENCHMARK_LABEL, BENCHMARK_URL } from '../../../content/benchmarkClaim';
import useReveal from '../useReveal';
import FurtherReading from '../FurtherReading';
import { DEFAULT_ROLE_READING } from '../blogPosts';
import AIEWall from '../sections/AIEWall';
import AIEControlSection from '../sections/AIEControlSection';
import AIEStepsStrip from '../sections/AIEStepsStrip';
import { ROLE_CONFIGS, ROLE_SLUGS } from './roleConfigs';
import './RoleFrames.css';
import './RoleLandingPageV2.css';

/**
 * RoleLandingPageV2 — the role-page SEO experiment layout (Nitish, Sep 23 2026;
 * PRD: Organization Drive Marketing/Role Pages/PRD_role_pages_v1.md section 10).
 * Renders ONLY for slugs in roles/v2/index.js (wave 1); every other role keeps
 * the original template. Content comes from one generated file per role
 * (roles/v2/<slug>.js, emitted by Maeve's gen_v2.py from the Rhea-ratified SEO
 * package); edit the generator, not the files.
 *
 * Reuses the REAL /ai-employees sections (AIEWall, AIEControlSection twice,
 * AIEStepsStrip) with role props. Rhea's build rules: H1 carries a real space
 * between role and accent; every pane's text is in the prerendered HTML; the
 * comparison is a real <table> with a caption; FAQPage JSON-LD = the visible
 * FAQ word for word; "Other roles" anchors are role names.
 */
const CONTROL_ICONS = [SafetyOutlined, SettingOutlined, EyeOutlined];
const WALL_SUB = 'Every screen below is the real work they do for you.';

const Section = ({ className = '', label, title, sub, children, narrow }) => {
  const [ref, visible] = useReveal();
  return (
    <section className={`aie-section rolp-section ${narrow ? 'rolp-section--narrow' : ''} ${className}`} ref={ref}>
      <div className={`aie-reveal ${visible ? 'is-visible' : ''}`}>
        {(label || title) && (
          <div className="aie-section-head rolp-section-head">
            {label && <span className="rolp-eyebrow">{label}</span>}
            {title && <h2 className="aie-section-title aie-display">{title}</h2>}
            {sub && <p className="aie-section-sub">{sub}</p>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
};

/** Definition block: the phrase in sentence 1, the dated benchmark fact linked.
 *  The label comes from content/benchmarkClaim.json; the generated roles/v2/*.js definitions carry it as
 *  plain text and marketing/scripts/check_benchmark_claim.py --verify fails if they drift from it. */
const Definition = ({ text }) => {
  const LB = BENCHMARK_LABEL;
  const i = text.indexOf(LB);
  if (i < 0) return <p>{text}</p>;
  return (
    <p>
      {text.slice(0, i)}
      <a href={BENCHMARK_URL} target="_blank" rel="noopener noreferrer">{LB}</a>
      {text.slice(i + LB.length)}
    </p>
  );
};

const RoleLandingPageV2 = ({ content: c, config, dark, glowRef, onHire, onTalk }) => {
  const canonicalUrl = `${SEO_DEFAULTS.url}/ai-employees/${c.slug}`;
  const ogImage = `${SEO_DEFAULTS.url}/assets/ai-employees/${c.slug}-og.jpg`;
  const faq = c.faq.map((f) => ({ q: f.q, a: f.a === '__COST_FAQ__' ? COST_FAQ_ANSWER : f.a, link: f.link }));
  const relatedSlugs = ROLE_SLUGS.filter((s) => s !== c.slug);
  const head = { eyebrow: 'See them work', title: `See your ${c.roleDisplay} at work`, sub: WALL_SUB };
  const noun = c.roleDisplay.replace(/^AI /, '');
  const whatTitle = `What is an AI ${noun === noun.toUpperCase() ? noun : noun.toLowerCase()}?`;
  const accentSub = (a, b) => (<>{a}{' '}<span className="aie-ctl__sub-accent">{b}</span></>);

  return (
    <>
      <Helmet>
        <title>{c.seo.title}</title>
        <meta name="description" content={c.seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={c.seo.title} />
        <meta property="og:description" content={c.seo.description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:alt" content={`${c.roleDisplay} illustration`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={c.seo.title} />
        <meta name="twitter:description" content={c.seo.description} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <JsonLd data={generateFAQSchema(faq.map((f) => ({ question: f.q, answer: f.link ? `${f.a} ${f.link.label}.` : f.a })))} />
      <JsonLd
        data={generateBreadcrumbSchema([
          { name: 'Home', url: `${SEO_DEFAULTS.url}/` },
          { name: 'AI Employees', url: `${SEO_DEFAULTS.url}/ai-employees` },
          { name: c.roleDisplay, url: canonicalUrl },
        ])}
      />

      <div className={`aie-landing rolp rolp-v2 ${dark ? 'dark' : ''}`}>
        {/* 1. Hero, centered: breadcrumb, pill, H1 + accent, pain sub, CTAs */}
        <section className="rolp-hero">
          <div className="rolp-hero__glow" aria-hidden="true" ref={glowRef} />
          <div className="rolp-hero__inner">
            <nav className="rolp-breadcrumb" aria-label="Breadcrumb">
              <Link to="/ai-employees">AI Employees</Link>
              <span aria-hidden="true">/</span>
              <span>{c.roleDisplay}</span>
            </nav>
            <span className="rolp-hero__pill">
              <span className="rolp-hero__pill-dot" aria-hidden="true" />
              CellCog AI Employee
            </span>
            <h1 className="rolp-hero__title aie-display">
              {/* NBSP, not ' ': react-snap's minifier strips a plain space before <br>,
                  so crawlers read "AI SDRhired" (Rhea, gate 3, Sep 23 2026). */}
              {c.roleDisplay}{'\u00a0'}
              <br />
              <span className="rolp-hero__title-accent">hired in minutes, works every day.</span>
            </h1>
            <p className="rolp-hero__sub">{c.painSub}</p>
            <div className="rolp-hero__ctas">
              <button type="button" className="aie-btn aie-btn-primary" onClick={() => onHire('hero')}>
                Get started <ArrowRightOutlined />
              </button>
              <button type="button" className="aie-btn aie-btn-ghost" onClick={() => onTalk('hero')}>
                Talk to us
              </button>
            </div>
          </div>
        </section>

        {/* 2. Definition: the one place the role is defined */}
        <Section label="The short answer" title={whatTitle} narrow className="rolp-v2-what">
          <div className="rolp-v2-answer"><Definition text={c.definition} /></div>
        </Section>

        {/* 3. The real AI Employees wall, role panes */}
        <AIEWall
          head={head}
          panes={c.panes}
          page={c.slug}
          sectionId="see-it-work"
          ctaLabel="Get started"
          onPrimarySticky={() => onHire('wall')}
        />

        {/* 4. What it does: 6 capability cards */}
        <Section label="Capabilities" title={`What your ${c.roleDisplay} does`}>
          <div className="rolp-v2-caps rolp-stagger">
            {c.capabilities.map((x, i) => (
              <div className="rolp-cap-card" key={x.title}>
                <span className="rolp-cap-num">{String(i + 1).padStart(2, '0')}</span>
                <h3>{x.title}</h3>
                <p>{x.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* 4b. Who it's for (Oct 23 2026 breadth pass): audience cards, restored from the pre-Sep-23 use cases */}
        {c.audience && (
          <Section label="Who it's for" title={c.audience.title}>
            <div className="rolp-uc-grid rolp-stagger">
              {c.audience.cards.map((u) => (
                <div className="rolp-uc-card" key={u.badge}>
                  <span className="rolp-uc-badge">{u.badge}</span>
                  <h3>{u.title}</h3>
                  <p>{u.body}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* 5. Set up in three steps (no HowTo schema) */}
        <AIEStepsStrip eyebrow="Setup" title="Set up in three steps." steps={c.steps} />

        {/* 6. Works with your tools */}
        <Section label="Tools" title="Works with your tools">
          <div className="rolp-v2-tools">
            {c.tools.map((t) => (
              <div className="rolp-v2-tool" key={t.name}>
                <span className="rolp-v2-logo"><img src={t.logo} alt="" loading="lazy" /></span>
                <span>{t.name}</span>
              </div>
            ))}
          </div>
          <p className="rolp-v2-more">and <Link to="/tools">{TOOL_COUNT} more tools</Link></p>
        </Section>

        {/* 7. Control */}
        <AIEControlSection
          statement={c.control.statement}
          sub={accentSub(c.control.sub, c.control.accent)}
          rows={c.control.rows.map((r, i) => ({ ...r, Icon: CONTROL_ICONS[i % CONTROL_ICONS.length] }))}
        />

        {/* 8. What it won't do: same layout, no icons */}
        <AIEControlSection
          eyebrow="Scope"
          statement="What they won't do."
          sub={accentSub(c.wont.sub, c.wont.accent)}
          rows={c.wont.rows}
          className="rolp-v2-wont"
        />

        {/* 9. Comparison: no human column */}
        <Section label="Compare" title={c.comparison.title} narrow>
          <div className="rolp-cmp-wrap">
            <table className="rolp-cmp">
              <caption className="rolp-v2-sr">{c.comparison.title}</caption>
              <thead>
                <tr>
                  <th aria-label="What gets done" />
                  {c.comparison.cols.map((col, i) => (
                    <th key={col} className={i === c.comparison.cols.length - 1 ? 'rolp-cmp__ai' : undefined}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {c.comparison.rows.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    {row.cells.map((cell, i) => (
                      <td key={`${row.label}-${i}`} className={i === row.cells.length - 1 ? 'rolp-cmp__ai' : undefined}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* 10. Prompts */}
        <Section label="Prompts" title="Things to ask them" sub="Copy, paste and adapt. Each one comes back as finished work." narrow>
          <div className="rolp-prompts rolp-stagger">
            {c.prompts.map((p, i) => (
              <div className="rolp-prompt" key={p.q}>
                <span className="rolp-prompt__num">{i + 1}</span>
                <div>
                  <p className="rolp-prompt__q">&ldquo;{p.q}&rdquo;</p>
                  <p className="rolp-prompt__a">You get: <b>{p.deliverable}</b></p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* 11. FAQ (8, JSON-LD above = this list verbatim) */}
        <Section label="FAQ" title="Frequently asked questions" narrow>
          <div className="rolp-faq">
            {faq.map((f, i) => (
              <details className="rolp-faq__item" key={f.q} open={i === 0}>
                <summary>{f.q}</summary>
                <p>{f.a}{f.link && (<>{' '}<Link to={f.link.href}>{f.link.label}</Link>.</>)}</p>
              </details>
            ))}
          </div>
        </Section>

        {/* 12. Other roles: role names as anchor text */}
        <Section label="Other roles" title="Other roles you can hire for">
          <div className="rolp-rel-grid rolp-stagger">
            {relatedSlugs.map((s) => (
              <Link className="rolp-rel-card" key={s} to={`/ai-employees/${s}`}>
                <span>{ROLE_CONFIGS[s].roleDisplay}</span>
                <ArrowRightOutlined />
              </Link>
            ))}
          </div>
        </Section>

        <Section label="Further reading" title="From the blog">
          <FurtherReading slugs={config.relatedPosts || DEFAULT_ROLE_READING} />
        </Section>

        {/* Close band */}
        <section className="rolp-close">
          <div className="rolp-close__glow" aria-hidden="true" />
          <div className="rolp-close__inner">
            <h2 className="aie-display">{c.ctaBand.title}</h2>
            <p>{c.ctaBand.body}</p>
            <div className="rolp-hero__ctas">
              <button type="button" className="aie-btn aie-btn-primary" onClick={() => onHire('close')}>
                Get started <ArrowRightOutlined />
              </button>
              <button type="button" className="aie-btn aie-btn-ghost" onClick={() => onTalk('close')}>
                Talk to us
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default RoleLandingPageV2;
